import React from 'react';
import { bool, node, string } from 'prop-types';
import classNames from 'classnames';
import { Field } from 'react-final-form';

import Icon from '../Icon/Icon';

import css from './FieldCheckbox.module.css';

/**
 * A checkbox from the Components board: the drawn box (ticked in the family colour), its label,
 * and the states unchecked, hover, checked, `indeterminate` ("some selected", a −), a required
 * error shown after the label ("I accept the terms · Required") and disabled. The real checkbox
 * stays underneath for the keyboard and screen readers. In a group, `showErrorText={false}`
 * leaves the error text to the group, which shows it once; the box still turns red.
 */
const FieldCheckboxComponent = props => {
  const {
    rootClassName,
    className,
    textClassName,
    id,
    label,
    indeterminate,
    disabled,
    showErrorText,
    ...rest
  } = props;

  // This is a workaround for a bug in Firefox & React Final Form.
  // https://github.com/final-form/react-final-form/issues/134
  const handleOnChange = (input, event) => {
    const { onBlur, onChange } = input;
    onChange(event);
    onBlur(event);
  };

  return (
    <Field type="checkbox" {...rest}>
      {({ input, meta }) => {
        const error = meta.touched && meta.error ? meta.error : null;
        const errorText = showErrorText ? error : null;
        const classes = classNames(rootClassName || css.root, className, {
          [css.hasError]: !!error,
        });
        return (
          <span className={classes}>
            <input
              id={id}
              className={css.input}
              {...input}
              disabled={disabled}
              // A DOM property, not an attribute: set it on every render
              ref={el => {
                if (el) el.indeterminate = !!indeterminate;
              }}
              aria-invalid={error ? true : undefined}
              aria-describedby={errorText ? `${id}-error` : undefined}
              onChange={event => handleOnChange(input, event)}
            />
            <label htmlFor={id} className={css.label}>
              <span className={css.box} aria-hidden="true">
                {indeterminate ? (
                  <Icon name="minus" size={14} />
                ) : input.checked ? (
                  <Icon name="check" size={14} />
                ) : null}
              </span>
              <span className={textClassName || css.text}>
                {label}
                {errorText ? (
                  <span id={`${id}-error`} className={css.error}>
                    {' · '}
                    {errorText}
                  </span>
                ) : null}
              </span>
            </label>
          </span>
        );
      }}
    </Field>
  );
};

FieldCheckboxComponent.defaultProps = {
  className: null,
  rootClassName: null,
  textClassName: null,
  label: null,
  indeterminate: false,
  disabled: false,
  showErrorText: true,
};

FieldCheckboxComponent.propTypes = {
  className: string,
  rootClassName: string,
  textClassName: string,

  // Id is needed to connect the label with input.
  id: string.isRequired,
  label: node,

  // Name groups several checkboxes to an array of selected values
  name: string.isRequired,

  // Checkbox needs a value that is passed forward when user checks the checkbox
  value: string.isRequired,

  // "Some selected": a minus instead of the tick, e.g. for a "select all" row
  indeterminate: bool,
  disabled: bool,
  // False in a group: the group shows the error once, under its rows
  showErrorText: bool,
};

export default FieldCheckboxComponent;
